// Lapisan render lalu lintas: memakai inti simulasi (trafficCore.js) dan melukis
// kendaraan dari katalog (paintCode). c: konteks engine
// { gridToScreen, cam, atmos, headlights, gridSize, city, drawOccluded }.
import { createTrafficCore } from "./trafficCore";
import { paintBuildingAt } from "./paint";
import { hashN } from "./sky";
import { bolehLukis, createLruSprite } from "./lruSprite";

export function createTraffic(c) {
  const core = createTrafficCore(c.gridSize);

  function drawFallback(ctx, x, y, zoom, seed) {
    const z = zoom * 1.8;
    ctx.fillStyle = "rgba(0,0,0,0.22)";
    ctx.beginPath(); ctx.ellipse(x, y + 2 * z, 12 * z, 5 * z, 0, 0, 7); ctx.fill();
    ctx.fillStyle = ["#EF4444", "#3B82F6", "#F59E0B", "#10B981"][Math.floor(hashN(seed) * 4) % 4];
    ctx.beginPath();
    ctx.moveTo(x - 12 * z, y); ctx.lineTo(x, y - 6 * z); ctx.lineTo(x + 12 * z, y); ctx.lineTo(x, y + 6 * z); ctx.closePath(); ctx.fill();
  }

  // ---- Cache sprite kendaraan (zoom rendah): paintCode dieksekusi sekali per
  // (aset, arah, zoom-bucket, malam, fase animasi) lalu hanya di-blit → jauh lebih
  // ringan saat puluhan mobil terlihat sekaligus. Zoom dekat tetap dilukis langsung.
  // Cache LRU (tanpa clear massal) + anggaran paintCode per frame → tidak ada hentakan berkala (lihat lruSprite.js).
  const SPRITE_MAX_ZOOM = 1.25;
  const spriteCache = createLruSprite(40e6); // ≈40 MB bitmap
  const animDefs = new WeakMap(); // def → apakah bodi memakai animasi waktu (t*)
  function getSprite(def, dir, zoom, night, t, seed) {
    const zb = Math.round(zoom * 20) / 20;
    const nb = night > 0.4 ? 1 : 0;
    if (!animDefs.has(def)) animDefs.set(def, /[^\w]t\s*\*/.test(def.paintCode.split("function propeller").pop()));
    const tb = animDefs.get(def) ? Math.floor(((t + seed * 0.37) * 8) % 16) : 0;
    const prefix = `${def.bid}|${dir}|${nb}|${tb}|`, key = prefix + zb;
    let sp = spriteCache.get(key);
    if (sp) return sp;
    if (!bolehLukis()) { // anggaran frame habis → pakai sprite zoom terdekat (diskalakan) sampai giliran berikutnya
      const dekat = spriteCache.nearest(prefix, zb);
      if (dekat) return { cv: dekat.cv, size: dekat.size * zb / dekat.zb };
    }
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const size = Math.ceil(96 * zb), cv = document.createElement("canvas");
    cv.width = cv.height = Math.ceil(size * dpr);
    const g = cv.getContext("2d");
    g.scale(dpr, dpr);
    paintBuildingAt(g, { ...def, category: "Kendaraan" }, size / 2, size / 2, zb, { time: t + seed * 0.37, night: nb ? 1 : 0, dir });
    sp = { cv, size, zb };
    spriteCache.set(key, sp);
    return sp;
  }

  // Mobil yang perlu digambar frame ini (tidak tersembunyi & tidak transparan penuh)
  function visible() { return core.cars.filter(m => !m.hidden && m.alpha > 0.01); }

  // Gambar satu mobil (culling layar, oklusi mask bangunan di depannya) + catat lampu depan/rem
  function drawOne(ctx, m, t, W, H) {
    const zoom = c.cam.zoom, pad = 100 * zoom;
    const [sx0, sy0] = c.gridToScreen(m.gx, m.gy);
    if (sx0 < -pad || sx0 > W + pad || sy0 < -pad || sy0 > H + pad) return;
    if (c.atmos.dark > 0.15) c.headlights.push({ gx: m.gx, gy: m.gy, x: sx0, y: sy0, dx: m.tx, dy: m.ty, a: m.alpha, brake: m.brake, half: m.half });
    const paint = (g, sx, sy) => {
      g.save();
      if (m.alpha < 1) g.globalAlpha = m.alpha;
      if (m.def && m.def.paintCode) {
        if (zoom <= SPRITE_MAX_ZOOM) {
          const sp = getSprite(m.def, m.dir, zoom, c.atmos.dark, t, m.seed);
          g.drawImage(sp.cv, sx - sp.size / 2, sy - sp.size / 2, sp.size, sp.size);
        } else {
          paintBuildingAt(g, { ...m.def, category: "Kendaraan" }, sx, sy, zoom, { time: t + m.seed * 0.37, night: c.atmos.dark, dir: m.dir });
        }
      } else drawFallback(g, sx, sy, zoom, m.seed);
      g.restore();
    };
    c.drawOccluded(m.gx, m.gy, 48 * zoom, paint);
  }

  return {
    build: (state, defs) => core.build(state, defs, c.city?.hour ?? 12),
    update: (dt, t) => core.update(dt, t, c.city?.hour ?? 12),
    visible, drawOne,
    get list() { return core.cars; }
  };
}
